import React from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, InjectedFormProps } from 'redux-form'
import { compose, Dispatch } from '@reduxjs/toolkit'

import { RootState } from 'typedRedux/configureStoreWithHistory'
import { updateCurrentUser } from 'actions/authActions'
import { renderTextField, Submit } from 'components/form'
import { validRequired, validEmail } from 'components/form/validate'
import InlineAlert from 'components/display/InlineAlert'
import IconInfoOutline from 'components/icons/InfoOutline'
import UserLocationField from '../UserLocationField'
import withCurrentVisit from '../withCurrentVisit'
import { FieldWrapper } from '../styles'

interface ConfirmEmailFormProps extends InjectedFormProps {
  auth: { loading: boolean; currentUser: RootState['auth']['currentUser'] }
  currentUser: RootState['auth']['currentUser']
  dispatch: Dispatch<any>
}

interface ConfirmEmailProps {
  onSuccess: () => any
}

interface VisitProps {
  location: {
    name: string
    lng: number
    lat: number
  }
}

const ConfirmEmail = ({
  onSuccess,
  handleSubmit,
  auth,
  currentUser,
  form,
  dispatch,
}: ConfirmEmailProps & ConfirmEmailFormProps) => {
  return (
    <form
      className="qa-confirm-email"
      onSubmit={handleSubmit((formValues: { user: { email: string } }) => {
        // @ts-ignore -- updateCurrentUser not typed
        dispatch(updateCurrentUser({ email: formValues.user.email })).then(
          onSuccess
        )
      })}
    >
      <FieldWrapper margin="medium">
        <InlineAlert variant="information">
          <IconInfoOutline />

          <p>
            {currentUser?.email ? (
              <>
                Facebook gave us this email address. Please confirm that this is
                one you want to use for your Riparide account. If it&apos;s not,
                you can change it now.
              </>
            ) : (
              <>
                Facebook didn&apos;t give us your email address. We need this to
                communicate with you about bookings. Please enter your email
                address now.
              </>
            )}
          </p>
        </InlineAlert>
      </FieldWrapper>

      <FieldWrapper>
        <Field
          label="Email"
          name="user[email]"
          type="email"
          component={renderTextField}
          validate={[validRequired, validEmail]}
          noMargin
        />
      </FieldWrapper>

      <FieldWrapper margin="medium">
        {/* @ts-ignore -- not typed yet */}
        <UserLocationField formName={form} noMargin />
      </FieldWrapper>

      <FieldWrapper>
        <Submit
          disabled={auth.loading}
          fullwidth
          uppercase={false}
          aria-label="Confirm Facebook login"
        >
          Confirm
        </Submit>
      </FieldWrapper>
    </form>
  )
}

const ConfirmEmailForm = reduxForm({
  form: 'ConfirmEmail',
  enableReinitialize: true,
})(ConfirmEmail)

const ConnectedConfirmEmailForm: React.FC<ConfirmEmailProps> = compose(
  withCurrentVisit,
  connect(
    (
      { auth }: { auth: ConfirmEmailFormProps['auth'] },
      { currentVisit }: { currentVisit: VisitProps }
    ) => ({
      auth,
      currentUser: auth.currentUser,
      initialValues: {
        user: {
          ...auth.currentUser,
          location: currentVisit?.location?.name,
          longitude: currentVisit?.location?.lng,
          latitude: currentVisit?.location?.lat,
        },
      },
    })
  )
)(ConfirmEmailForm)

export default ConnectedConfirmEmailForm
